'use client';

import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { doctorsApi, filesApi } from '@/api';
import type { DoctorListItem, Specialty } from '@/api';
import { Header } from '@/landing/v3/sections/Header';
import {
  Search,
  Star,
  MapPin,
  Video,
  Home,
  BadgeCheck,
  Stethoscope,
  Users,
  Navigation,
} from 'lucide-react';

const EASING_OUT = 'cubic-bezier(0.23, 1, 0.32, 1)';

function getDoctorAgeBadge(doctor: DoctorListItem): string {
  const minAge = doctor.minAge !== undefined && doctor.minAge !== null ? Number(doctor.minAge) : null;
  const maxAge = doctor.maxAge !== undefined && doctor.maxAge !== null ? Number(doctor.maxAge) : null;

  if (minAge !== null && maxAge !== null) {
    if (minAge === 0 && maxAge <= 17) {
      return `0 a ${maxAge} anos`;
    }
    if (minAge >= 18 && maxAge >= 90) {
      return 'Adultos e Idosos';
    }
    return `${minAge} a ${maxAge} anos`;
  }
  if (minAge !== null) {
    if (minAge >= 18) {
      return `Adultos (${minAge}+)`;
    }
    return `${minAge}+ anos`;
  }
  if (maxAge !== null) {
    return `Até ${maxAge} anos`;
  }
  if (doctor.allowedAgeGroups && doctor.allowedAgeGroups.length > 0) {
    const formatted = doctor.allowedAgeGroups.map((g) => {
      const upper = g.toUpperCase();
      if (upper.includes('PED') || upper.includes('INFANT') || upper.includes('KID') || upper.includes('CRIAN')) return 'Infantil';
      if (upper.includes('TEEN') || upper.includes('ADOL')) return 'Adolescentes';
      if (upper.includes('ADULT')) return 'Adultos';
      if (upper.includes('ELDER') || upper.includes('IDOS')) return 'Idosos';
      return g;
    });
    return formatted.join(', ');
  }
  return 'Todas as idades';
}

interface DoctorCardProps {
  doctor: DoctorListItem;
  index: number;
}

function DoctorCard({ doctor, index }: DoctorCardProps) {
  const navigate = useNavigate();
  const [imageError, setImageError] = useState(false);

  const rawPhoto = doctor.profile?.photoUrl;
  const photoUrl = rawPhoto ? filesApi.getFileUrl(rawPhoto) : null;
  const hasPhoto = Boolean(photoUrl && !imageError);

  const displayName = doctor.profile?.displayName || 'Médico';
  const specialty = doctor.primarySpecialty?.name || 'Especialista';
  const experience = doctor.experience || 0;
  const tagline = doctor.profile?.tagline || '';

  const location = `CRM ${doctor.crm || ''}/${doctor.crmState || 'BR'}`;

  const priceValue = doctor.consultationPrice != null
    ? (doctor.consultationPrice >= 1000 ? doctor.consultationPrice / 100 : doctor.consultationPrice)
    : 150;
  const formattedPrice = new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(priceValue);

  const hasExplicitModalities =
    doctor.hasTelemedicine !== undefined ||
    doctor.hasInPerson !== undefined ||
    doctor.hasHomeCare !== undefined;

  const hasVideo = hasExplicitModalities
    ? Boolean(doctor.hasTelemedicine)
    : Boolean(doctor.isExpressEnabled);
  const hasInPerson = Boolean(doctor.hasInPerson);
  const hasHome = Boolean(doctor.hasHomeCare);

  const hasRating = Boolean(
    doctor.statistics?.averageRating &&
    (doctor.statistics?.totalRatings ?? 0) > 0
  );

  const getDoctorInitials = (name: string) => {
    const cleaned = name.replace(/^(dr\.|dra\.|dr|dra)\s+/i, '').trim();
    const parts = cleaned.split(/\s+/).filter(Boolean);
    const first = parts[0] ?? '';
    const last = parts[parts.length - 1] ?? '';
    if (!first) return 'Dr.';
    if (parts.length === 1) return `Dr. ${first.slice(0, 2).toUpperCase()}`;
    return `Dr. ${first[0]?.toUpperCase() ?? ''}${last[0]?.toUpperCase() ?? ''}`;
  };

  const initials = getDoctorInitials(displayName);

  return (
    <div
      className="opacity-0"
      style={{
        animation: `fadeInUp 0.5s ${EASING_OUT} forwards`,
        animationDelay: `${index * 80}ms`,
      }}
    >
      <div
        onClick={() => navigate(`/medicos/${doctor.id}`)}
        className="group relative rounded-3xl overflow-hidden cursor-pointer card-3d bg-slate-900"
        style={{ aspectRatio: '3 / 4' }}
      >
        {hasPhoto ? (
          <img
            src={photoUrl!}
            alt={displayName}
            onError={() => setImageError(true)}
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
        ) : (
          <div className="absolute inset-0 w-full h-full flex items-center justify-center bg-slate-900 bg-gradient-to-br from-primary/20 to-primary/40 text-primary font-bold text-4xl select-none pb-16">
            {initials}
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />

        <div className="absolute top-0 left-0 right-0 h-24 bg-gradient-to-b from-black/30 to-transparent" />

        <div className="absolute top-4 left-4 right-4 flex items-start justify-between z-10">
          <div className="flex flex-col gap-2">
            {doctor.distanceKm != null && !isNaN(Number(doctor.distanceKm)) && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-600/95 backdrop-blur-md text-white text-xs font-bold shadow-md animate-in fade-in">
                <MapPin className="w-3.5 h-3.5" />
                {Number(doctor.distanceKm).toFixed(1)} km de você
              </span>
            )}
            {doctor.isOnline && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/15 backdrop-blur-sm text-white text-xs font-medium">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                Online agora
              </span>
            )}
          </div>
          {hasRating ? (
            <div className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-white/15 backdrop-blur-sm">
              <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
              <span className="text-white text-sm font-bold">
                {Number(doctor.statistics?.averageRating).toFixed(1)}
              </span>
            </div>
          ) : (
            <div className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-white/15 backdrop-blur-sm">
              <span className="text-white/90 text-xs font-medium">Novo profissional</span>
            </div>
          )}
        </div>

        <div className="absolute bottom-0 left-0 right-0 p-5 z-10">
          <div className="flex items-center gap-1.5 mb-1">
            <h3 className="text-white text-lg font-bold">{displayName}</h3>
            <BadgeCheck className="w-4 h-4 text-primary-light flex-shrink-0" />
          </div>
          <p className="text-white/80 text-sm font-medium">{specialty} · {experience} anos</p>
          <div className="flex items-center gap-1 mt-1 text-white/60 text-xs">
            <MapPin className="w-3 h-3" />
            {location}
          </div>
          {tagline && (
            <p className="text-white/50 text-xs mt-3 line-clamp-2 italic">"{tagline}"</p>
          )}

          <div className="flex flex-wrap items-center gap-1.5 mt-3">
            <span className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-primary/25 text-white text-[10px] font-semibold border border-primary/40 backdrop-blur-sm shadow-sm">
              <Users className="w-2.5 h-2.5 text-primary-light" />
              {getDoctorAgeBadge(doctor)}
            </span>
            {hasInPerson && (
              <span className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/10 backdrop-blur-sm text-white/80 text-[10px] font-medium">
                <MapPin className="w-2.5 h-2.5" />
                Presencial
              </span>
            )}
            {hasVideo && (
              <span className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/10 backdrop-blur-sm text-white/80 text-[10px] font-medium">
                <Video className="w-2.5 h-2.5" />
                Tele
              </span>
            )}
            {hasHome && (
              <span className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/10 backdrop-blur-sm text-white/80 text-[10px] font-medium">
                <Home className="w-2.5 h-2.5" />
                Domiciliar
              </span>
            )}
          </div>

          <div className="mt-4 flex items-center justify-between">
            <div>
              <span className="text-white/50 text-xs">A partir de</span>
              <p className="text-white text-lg font-bold">{formattedPrice}</p>
            </div>
            <div className="px-5 py-2.5 rounded-full bg-white text-primary text-sm font-bold group-hover:bg-primary group-hover:text-white transition-all duration-300 shadow-lg">
              Ver Perfil
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function DoctorsPage() {
  const [searchParams] = useSearchParams();
  const urlModalityParam = (searchParams.get('modality') || searchParams.get('type') || '').toUpperCase();
  const initialModality =
    urlModalityParam === 'DOMICILIAR' || urlModalityParam === 'HOME'
      ? 'DOMICILIAR'
      : urlModalityParam === 'PRESENCIAL' || urlModalityParam === 'OFFICE' || urlModalityParam === 'CONSULTORIO'
      ? 'PRESENCIAL'
      : urlModalityParam === 'TELEMEDICINA' || urlModalityParam === 'VIDEO'
      ? 'TELEMEDICINA'
      : 'ALL';

  const [doctors, setDoctors] = useState<DoctorListItem[]>([]);
  const [specialties, setSpecialties] = useState<Specialty[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [debouncedSearch, setDebouncedSearch] = useState(searchParams.get('q') || '');
  const [selectedSpecialty, setSelectedSpecialty] = useState<string | null>(searchParams.get('specialty') || null);
  const [selectedModality, setSelectedModality] = useState<'ALL' | 'TELEMEDICINA' | 'PRESENCIAL' | 'DOMICILIAR'>(initialModality);
  const [selectedAgeFilter, setSelectedAgeFilter] = useState<'ALL' | 'INFANTIL' | 'ADULTOS' | 'IDOSOS'>('ALL');

  // Sincroniza a modalidade quando parâmetros de URL mudam (ex: links diretos de afiliados)
  useEffect(() => {
    if (urlModalityParam) {
      if (urlModalityParam === 'DOMICILIAR' || urlModalityParam === 'HOME') {
        setSelectedModality('DOMICILIAR');
      } else if (urlModalityParam === 'PRESENCIAL' || urlModalityParam === 'OFFICE' || urlModalityParam === 'CONSULTORIO') {
        setSelectedModality('PRESENCIAL');
      } else if (urlModalityParam === 'TELEMEDICINA' || urlModalityParam === 'VIDEO') {
        setSelectedModality('TELEMEDICINA');
      }
    }
  }, [urlModalityParam]);

  // Recupera CEP persistido em localStorage se disponível
  const [patientCep, setPatientCep] = useState<string>(() => {
    return searchParams.get('cep') || localStorage.getItem('medikei_patient_cep') || '';
  });

  // Recupera coordenadas GPS persistidas em localStorage se disponíveis
  const [userCoords, setUserCoords] = useState<{ lat: number; lng: number } | null>(() => {
    try {
      const saved = localStorage.getItem('medikei_patient_coords');
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });
  const [isLocating, setIsLocating] = useState(false);

  // Salva CEP em localStorage para manter localização ativa por geolocalização
  useEffect(() => {
    if (patientCep && patientCep.trim().length >= 8) {
      localStorage.setItem('medikei_patient_cep', patientCep.trim());
    }
  }, [patientCep]);

  // Salva coordenadas em localStorage para manter localização ativa por geolocalização
  useEffect(() => {
    if (userCoords) {
      localStorage.setItem('medikei_patient_coords', JSON.stringify(userCoords));
    }
  }, [userCoords]);

  const handleUseCurrentLocation = useCallback(() => {
    if (!navigator.geolocation) {
      alert('Geolocalização não é suportada pelo seu navegador.');
      return;
    }
    setIsLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const coords = {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
        };
        setUserCoords(coords);
        localStorage.setItem('medikei_patient_coords', JSON.stringify(coords));
        setIsLocating(false);
      },
      (err) => {
        console.error('Erro ao obter localização:', err);
        setIsLocating(false);
      },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  }, []);

  // Solicita localização automaticamente se o usuário selecionar Domiciliar ou Presencial e ainda não tiver
  useEffect(() => {
    if (
      (selectedModality === 'PRESENCIAL' || selectedModality === 'DOMICILIAR') &&
      !userCoords &&
      !patientCep &&
      navigator.geolocation
    ) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const coords = {
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
          };
          setUserCoords(coords);
          localStorage.setItem('medikei_patient_coords', JSON.stringify(coords));
        },
        () => {
          // Permissão não concedida, o usuário usará o campo de CEP
        },
        { enableHighAccuracy: false, timeout: 5000 },
      );
    }
  }, [selectedModality, userCoords, patientCep]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 350);

    return () => {
      clearTimeout(handler);
    };
  }, [searchQuery]);

  useEffect(() => {
    const loadSpecialties = async () => {
      try {
        const data = await doctorsApi.getSpecialties();
        setSpecialties(data);
      } catch {
        // Handle error silently
      }
    };
    loadSpecialties();
  }, []);

  useEffect(() => {
    const loadDoctors = async () => {
      setIsLoading(true);
      try {
        const isGeoSearch = (selectedModality === 'PRESENCIAL' || selectedModality === 'DOMICILIAR') && (Boolean(userCoords) || Boolean(patientCep.trim()));
        const cityParam = searchParams.get('city') || '';
        const data = await doctorsApi.getDoctors({
          search: debouncedSearch.trim() || undefined,
          specialty: selectedSpecialty || undefined,
          city: cityParam.trim() || undefined,
          modality: selectedModality !== 'ALL' ? selectedModality : undefined,
          latitude: userCoords?.lat,
          longitude: userCoords?.lng,
          cep: patientCep.trim() || undefined,
          orderBy: isGeoSearch ? 'distance' : undefined,
          page: 1,
          limit: 30,
        });
        setDoctors(data.data);
      } catch {
        setDoctors([]);
      } finally {
        setIsLoading(false);
      }
    };
    loadDoctors();
  }, [debouncedSearch, selectedSpecialty, selectedModality, userCoords, patientCep]);

  const filteredDoctors = doctors.filter((doctor) => {
    if (selectedModality !== 'ALL') {
      const hasExplicit =
        doctor.hasTelemedicine !== undefined ||
        doctor.hasInPerson !== undefined ||
        doctor.hasHomeCare !== undefined;

      if (selectedModality === 'TELEMEDICINA') {
        const allowed = hasExplicit ? Boolean(doctor.hasTelemedicine) : Boolean(doctor.isExpressEnabled);
        if (!allowed) return false;
      } else if (selectedModality === 'PRESENCIAL') {
        if (!doctor.hasInPerson) return false;
      } else if (selectedModality === 'DOMICILIAR') {
        if (!doctor.hasHomeCare) return false;
      }
    }

    if (selectedAgeFilter !== 'ALL') {
      const minAge = doctor.minAge !== undefined && doctor.minAge !== null ? Number(doctor.minAge) : null;
      const maxAge = doctor.maxAge !== undefined && doctor.maxAge !== null ? Number(doctor.maxAge) : null;
      const groups = doctor.allowedAgeGroups?.map((g) => g.toUpperCase()) || [];
      const specName = doctor.primarySpecialty?.name?.toLowerCase() || '';

      if (selectedAgeFilter === 'INFANTIL') {
        if (minAge !== null && minAge >= 18) return false;
        const isPediatric =
          specName.includes('pediatr') ||
          groups.some((g) => g.includes('PED') || g.includes('KID') || g.includes('INFANT') || g.includes('CRIAN')) ||
          (minAge !== null && minAge < 18) ||
          (maxAge !== null && maxAge <= 17);
        const hasNoRestrictions = minAge === null && maxAge === null && groups.length === 0;
        if (!isPediatric && !hasNoRestrictions) return false;
      } else if (selectedAgeFilter === 'ADULTOS') {
        if (maxAge !== null && maxAge < 18) return false;
        if (minAge !== null && minAge > 65) return false;
      } else if (selectedAgeFilter === 'IDOSOS') {
        if (maxAge !== null && maxAge < 60) return false;
      }
    }

    return true;
  });

  const handleClearFilters = () => {
    setSearchQuery('');
    setDebouncedSearch('');
    setSelectedSpecialty(null);
    setSelectedModality('ALL');
    setSelectedAgeFilter('ALL');
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <Header />

      <section className="pt-8 pb-8 flex-1">
        <div className="container">
          <div style={{ opacity: 1, transform: 'none' }}>
            <h1 className="text-3xl lg:text-4xl font-bold mb-2">
              Encontre seu <span className="text-gradient">médico ideal</span>
            </h1>
            <p className="text-muted-foreground text-lg">
              {isLoading ? 'Carregando...' : `${filteredDoctors.length} profissionais disponíveis`}
            </p>
          </div>
        </div>
      </section>

      <section className="sticky top-[72px] z-30 bg-background/80 backdrop-blur-xl border-b border-border/50 py-4">
        <div className="container">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1 flex items-center gap-3 px-4 py-3 bg-white rounded-xl border border-border/50">
              <Search className="w-5 h-5 text-muted-foreground shrink-0" />
              <input
                type="text"
                placeholder="Buscar por nome ou especialidade..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-transparent outline-none text-sm"
              />
            </div>

            <div className="flex items-center gap-2 overflow-x-auto no-scrollbar">
              <button
                type="button"
                onClick={() => setSelectedModality('ALL')}
                className={`flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-xs font-medium transition-all shrink-0 ${
                  selectedModality === 'ALL'
                    ? 'bg-primary text-white shadow-sm'
                    : 'bg-white border border-border/50 text-foreground/70 hover:border-primary/30'
                }`}
              >
                Todas
              </button>
              <button
                type="button"
                onClick={() => setSelectedModality(selectedModality === 'PRESENCIAL' ? 'ALL' : 'PRESENCIAL')}
                className={`flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-xs font-medium transition-all shrink-0 ${
                  selectedModality === 'PRESENCIAL'
                    ? 'bg-primary text-white shadow-sm'
                    : 'bg-white border border-border/50 text-foreground/70 hover:border-primary/30'
                }`}
              >
                <MapPin className="w-3.5 h-3.5" />
                Presencial
              </button>
              <button
                type="button"
                onClick={() => setSelectedModality(selectedModality === 'TELEMEDICINA' ? 'ALL' : 'TELEMEDICINA')}
                className={`flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-xs font-medium transition-all shrink-0 ${
                  selectedModality === 'TELEMEDICINA'
                    ? 'bg-primary text-white shadow-sm'
                    : 'bg-white border border-border/50 text-foreground/70 hover:border-primary/30'
                }`}
              >
                <Video className="w-3.5 h-3.5" />
                Telemedicina
              </button>
              <button
                type="button"
                onClick={() => setSelectedModality(selectedModality === 'DOMICILIAR' ? 'ALL' : 'DOMICILIAR')}
                className={`flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-xs font-medium transition-all shrink-0 ${
                  selectedModality === 'DOMICILIAR'
                    ? 'bg-primary text-white shadow-sm'
                    : 'bg-white border border-border/50 text-foreground/70 hover:border-primary/30'
                }`}
              >
                <Home className="w-3.5 h-3.5" />
                Domiciliar
              </button>
            </div>
          </div>

          {(selectedModality === 'PRESENCIAL' || selectedModality === 'DOMICILIAR') && (
            <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-border/40 text-xs animate-in fade-in duration-200">
              <span className="font-semibold text-foreground flex items-center gap-1.5 shrink-0">
                <Navigation className="w-3.5 h-3.5 text-primary" />
                {selectedModality === 'DOMICILIAR'
                  ? 'Sua localização para Consulta em Casa:'
                  : 'Sua localização para Consultório Presencial:'}
              </span>
              <div className="flex items-center gap-2 flex-1 min-w-[200px] max-w-xs">
                <input
                  type="text"
                  value={patientCep}
                  onChange={(e) => setPatientCep(e.target.value)}
                  placeholder="Informe seu CEP (ex: 01310-100)"
                  maxLength={9}
                  className="h-8 px-3 rounded-lg border border-border/70 bg-white dark:bg-slate-900 text-xs w-full focus:outline-none focus:ring-1 focus:ring-primary"
                />
              </div>
              <button
                type="button"
                onClick={handleUseCurrentLocation}
                disabled={isLocating}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                  userCoords
                    ? 'bg-emerald-600 text-white shadow-sm'
                    : 'bg-primary/10 text-primary hover:bg-primary/20'
                }`}
              >
                <MapPin className="w-3.5 h-3.5" />
                {isLocating ? 'Obtendo GPS...' : userCoords ? '✓ GPS Ativo' : 'Usar meu local'}
              </button>
              {(userCoords || patientCep.trim()) && (
                <span className="text-[11px] text-muted-foreground flex items-center gap-1">
                  • Raio de proximidade geolocalizado ativo
                </span>
              )}
            </div>
          )}

          <div className="flex gap-2 mt-3 overflow-x-auto no-scrollbar pb-1">
            <button
              onClick={() => setSelectedSpecialty(null)}
              className={`shrink-0 px-4 py-2 rounded-full text-xs font-medium transition-all ${
                !selectedSpecialty
                  ? 'bg-primary text-white'
                  : 'bg-white border border-border/50 text-foreground/70 hover:border-primary/30'
              }`}
            >
              Todas
            </button>
            {specialties.map((specialty) => (
              <button
                key={specialty.id}
                onClick={() => setSelectedSpecialty(specialty.id)}
                className={`shrink-0 px-4 py-2 rounded-full text-xs font-medium transition-all ${
                  selectedSpecialty === specialty.id
                    ? 'bg-primary text-white'
                    : 'bg-white border border-border/50 text-foreground/70 hover:border-primary/30'
                }`}
              >
                {specialty.name}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2 mt-3 overflow-x-auto no-scrollbar pb-1 border-t border-border/40 pt-3">
            <span className="text-xs text-muted-foreground font-medium flex items-center gap-1 mr-1 shrink-0">
              <Users className="w-3.5 h-3.5" />
              Faixa etária:
            </span>
            <button
              type="button"
              onClick={() => setSelectedAgeFilter('ALL')}
              className={`shrink-0 px-3.5 py-1.5 rounded-full text-xs font-medium transition-all ${
                selectedAgeFilter === 'ALL'
                  ? 'bg-primary text-white shadow-sm'
                  : 'bg-white border border-border/50 text-foreground/70 hover:border-primary/30'
              }`}
            >
              Todas as idades
            </button>
            <button
              type="button"
              onClick={() => setSelectedAgeFilter('INFANTIL')}
              className={`shrink-0 px-3.5 py-1.5 rounded-full text-xs font-medium transition-all ${
                selectedAgeFilter === 'INFANTIL'
                  ? 'bg-primary text-white shadow-sm'
                  : 'bg-white border border-border/50 text-foreground/70 hover:border-primary/30'
              }`}
            >
              Infantil/Pediatria
            </button>
            <button
              type="button"
              onClick={() => setSelectedAgeFilter('ADULTOS')}
              className={`shrink-0 px-3.5 py-1.5 rounded-full text-xs font-medium transition-all ${
                selectedAgeFilter === 'ADULTOS'
                  ? 'bg-primary text-white shadow-sm'
                  : 'bg-white border border-border/50 text-foreground/70 hover:border-primary/30'
              }`}
            >
              Adultos
            </button>
            <button
              type="button"
              onClick={() => setSelectedAgeFilter('IDOSOS')}
              className={`shrink-0 px-3.5 py-1.5 rounded-full text-xs font-medium transition-all ${
                selectedAgeFilter === 'IDOSOS'
                  ? 'bg-primary text-white shadow-sm'
                  : 'bg-white border border-border/50 text-foreground/70 hover:border-primary/30'
              }`}
            >
              Idosos
            </button>
          </div>
        </div>
      </section>

      <section className="py-8 pb-20">
        <div className="container">
          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div
                  key={i}
                  className="rounded-3xl overflow-hidden animate-pulse"
                  style={{ aspectRatio: '3 / 4', backgroundColor: '#e5e7eb' }}
                />
              ))}
            </div>
          ) : filteredDoctors.length === 0 ? (
            <div className="text-center py-20 px-4 max-w-md mx-auto flex flex-col items-center">
              <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center text-primary mb-4">
                <Stethoscope className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-bold text-foreground mb-2">Nenhum médico encontrado</h3>
              <p className="text-muted-foreground text-sm mb-6 text-center">
                Tente ajustar os filtros ou a especialidade selecionada
              </p>
              <button
                type="button"
                onClick={handleClearFilters}
                className="px-6 py-2.5 rounded-full bg-primary text-white text-sm font-medium hover:bg-primary/90 transition-all shadow-sm"
              >
                Limpar filtros
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredDoctors.map((doctor, index) => (
                <DoctorCard key={doctor.id} doctor={doctor} index={index} />
              ))}
            </div>
          )}
        </div>
      </section>

      <footer className="bg-neutral-950 text-white relative overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[1px] bg-gradient-to-r from-transparent via-primary/50 to-transparent" />
        <div className="container py-16 lg:py-20">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
            <div className="lg:col-span-1">
              <div className="flex items-center gap-2.5 mb-5">
                <img
                  alt="Medikei"
                  className="h-10 w-10 rounded-xl"
                  src="https://d2xsxph8kpxj0f.cloudfront.net/310419663032066874/kJEPveuZ37rzBLMCBfLcMY/medikei_logo_m-VHPBKWLPnJCZaQfwBKpJpn.webp"
                />
                <span className="text-2xl font-bold">
                  Medi<span className="text-primary">kei</span>
                </span>
              </div>
              <p className="text-white/50 text-sm leading-relaxed mb-6">
                A plataforma que conecta você aos melhores médicos do Brasil. Consultas presenciais, telemedicina e atendimento domiciliar.
              </p>
            </div>

            <div>
              <h4 className="font-semibold text-sm uppercase tracking-wider text-white/40 mb-5">
                Plataforma
              </h4>
              <ul className="space-y-3">
                <li>
                  <span className="text-sm text-white/60 hover:text-white transition-colors cursor-pointer">
                    Encontrar Médicos
                  </span>
                </li>
                <li>
                  <span className="text-sm text-white/60 hover:text-white transition-colors cursor-pointer">
                    Telemedicina
                  </span>
                </li>
                <li>
                  <span className="text-sm text-white/60 hover:text-white transition-colors cursor-pointer">
                    Atendimento Domiciliar
                  </span>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-sm uppercase tracking-wider text-white/40 mb-5">
                Para Médicos
              </h4>
              <ul className="space-y-3">
                <li>
                  <span className="text-sm text-white/60 hover:text-white transition-colors cursor-pointer">
                    Cadastre-se
                  </span>
                </li>
                <li>
                  <span className="text-sm text-white/60 hover:text-white transition-colors cursor-pointer">
                    Planos e Preços
                  </span>
                </li>
                <li>
                  <span className="text-sm text-white/60 hover:text-white transition-colors cursor-pointer">
                    Dashboard Médico
                  </span>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-sm uppercase tracking-wider text-white/40 mb-5">
                Contato
              </h4>
              <ul className="space-y-4">
                <li className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-white/5 flex items-center justify-center">
                    <span className="text-primary text-xs">@</span>
                  </div>
                  <span className="text-sm text-white/60">contato@medikei.com.br</span>
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-white/5 flex items-center justify-center">
                    <span className="text-primary">📞</span>
                  </div>
                  <span className="text-sm text-white/60">(11) 9 9999-0000</span>
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-white/5 flex items-center justify-center">
                    <MapPin className="w-4 h-4 text-primary" />
                  </div>
                  <span className="text-sm text-white/60">São Paulo, SP</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-16 pt-8 border-t border-white/5 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-xs text-white/30">
              © 2026 Medikei. Todos os direitos reservados.
            </p>
            <div className="flex items-center gap-1 text-xs text-white/30">
              Feito com ❤️ para transformar a saúde
            </div>
          </div>
        </div>
      </footer>

      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .no-scrollbar::-webkit-scrollbar {
          display: none;
        }
        .no-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .animate-pulse {
          animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </div>
  );
}
